import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiHelpCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-brand-light/30">
      <button
        className="w-full py-6 flex items-center justify-between text-left group"
        onClick={onClick}
      >
        <span className={`font-serif text-lg md:text-xl transition-colors duration-300 ${isOpen ? 'text-brand-olive' : 'text-brand-dark group-hover:text-brand-olive'}`}>
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className={`${isOpen ? 'text-brand-olive' : 'text-brand-light'}`}
        >
          <SafeIcon icon={FiChevronDown} className="text-xl" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-brand-sage font-light leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What are your check-in and check-out times?",
      answer: "Our standard check-in time is 2:00 PM and check-out is at 12:00 PM. If you need an early check-in or late check-out, please let us know in advance, and we will do our best to accommodate you based on room availability."
    },
    {
      question: "Is parking available at the apartelle?",
      answer: "Since we are located in the heart of the town proper, street parking is available nearby. We recommend coordinating with us prior to your arrival so we can assist you with the best available spots."
    },
    {
      question: "Do you allow pets in the rooms?",
      answer: "Yes! We are proud to be a pet-friendly space. We understand that pets are part of the family. We just ask that you inform us beforehand and ensure your furry friends are well-behaved during the stay."
    },
    {
      question: "How do I secure a booking?",
      answer: "To secure your dates, we require a 50% downpayment. We accept payments via GCash, Maya, and Bank Transfers. The remaining balance can be settled upon check-in."
    },
    {
      question: "Is the apartelle accessible for elderly guests?",
      answer: "R. VENUE Apartelle is located on the 3rd floor of the building. Please note that access is via stairs. Our staff is always happy to assist with luggage to make your arrival as comfortable as possible."
    },
    {
      question: "Are you near the Lucban Church and Plaza?",
      answer: "Yes, we are in the absolute center of town! The San Luis Obispo Parish Church and the Town Plaza are just a 2-minute walk from our doorstep."
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-16">
          <span className="font-script text-3xl text-brand-sage mb-2 block">Common Questions</span>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-olive uppercase tracking-wider">Frequently Asked</h2>
        </div>

        <div className="bg-brand-cream/20 rounded-sm p-2 md:p-8">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-brand-sage font-light italic flex items-center justify-center gap-2">
            <SafeIcon icon={FiHelpCircle} className="text-brand-olive" />
            Have more questions? <Link to="/contact" className="text-brand-olive font-bold hover:underline">Contact our team</Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;